import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, CheckCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const TenantRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    company_name: '',
    subdomain: '',
    contact_name: '',
    contact_email: '',
    phone: '',
    password: '',
    confirm_password: '',
    subscription_plan: 'business',
    branches_count: 1,
    logo_url: ''
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const res = await axios.get('/subscription-plans');
      setPlans(res.data);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate subdomain from company name
    if (name === 'company_name') {
      const subdomain = value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/--+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({
        ...prev,
        subdomain: subdomain
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/tenant/register', {
        company_name: formData.company_name,
        subdomain: formData.subdomain,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        phone: formData.phone,
        password: formData.password,
        subscription_plan: formData.subscription_plan,
        branches_count: parseInt(formData.branches_count),
        logo_url: formData.logo_url
      });

      if (response.data.success) {
        toast.success('Registrierung erfolgreich! Sie werden weitergeleitet...');
        setTimeout(() => {
          navigate('/', { 
            state: { 
              message: `Willkommen! Melden Sie sich an unter ${formData.subdomain}.flotto.app` 
            } 
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMsg = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getPlanPrice = (plan) => {
    if (plan.plan_id === 'plan_starter') return '9,90€';
    if (plan.plan_id === 'plan_business') return '19,90€';
    if (plan.plan_id === 'plan_enterprise') return '29,90€';
    return '';
  };

  const getPlanColor = (plan) => {
    if (plan.plan_id === 'plan_starter') return 'border-green-700 bg-green-900/10';
    if (plan.plan_id === 'plan_business') return 'border-blue-700 bg-blue-900/10';
    if (plan.plan_id === 'plan_enterprise') return 'border-purple-700 bg-purple-900/10';
    return '';
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black uppercase tracking-tight text-white mb-4">
            Starten Sie mit Flotto
          </h1>
          <p className="text-xl text-zinc-400">
            Moderne Fuhrparkverwaltung für Ihr Unternehmen
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Registration Form */}
          <div className="lg:col-span-2">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Kostenlos registrieren</CardTitle>
                <CardDescription>14 Tage kostenlos testen, keine Kreditkarte erforderlich</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Unternehmensangaben</h3>
                    
                    <div>
                      <Label htmlFor="company_name">Unternehmensname *</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Mein Restaurant"
                        className="bg-zinc-950 border-zinc-800 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="subdomain">Ihre Subdomain *</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="subdomain"
                          name="subdomain"
                          value={formData.subdomain}
                          onChange={handleChange}
                          placeholder="mein-restaurant"
                          className="bg-zinc-950 border-zinc-800 text-white"
                          pattern="[a-z0-9-]+"
                          required
                        />
                        <span className="text-zinc-400 whitespace-nowrap">.flotto.app</span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        Nur Kleinbuchstaben, Zahlen und Bindestriche
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="phone">Telefonnummer</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+49 123 456789"
                        className="bg-zinc-950 border-zinc-800 text-white"
                      />
                    </div>
                  </div>

                  {/* Contact Person */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Kontaktperson</h3>
                    
                    <div>
                      <Label htmlFor="contact_name">Vollständiger Name *</Label>
                      <Input
                        id="contact_name"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        placeholder="Max Mustermann"
                        className="bg-zinc-950 border-zinc-800 text-white"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact_email">E-Mail-Adresse *</Label>
                      <Input
                        id="contact_email"
                        name="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={handleChange}
                        placeholder="max@mein-restaurant.de"
                        className="bg-zinc-950 border-zinc-800 text-white"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="password">Passwort *</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="bg-zinc-950 border-zinc-800 text-white"
                          minLength={8}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm_password">Passwort bestätigen *</Label>
                        <Input
                          id="confirm_password"
                          name="confirm_password"
                          type="password"
                          value={formData.confirm_password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="bg-zinc-950 border-zinc-800 text-white"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Plan Selection */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Abo-Plan wählen</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {plans.map((plan) => (
                        <label
                          key={plan.plan_id}
                          className={`
                            cursor-pointer p-4 rounded-lg border-2 transition-all
                            ${formData.subscription_plan === plan.plan_id.replace('plan_', '') 
                              ? getPlanColor(plan) 
                              : 'border-zinc-800 bg-zinc-900/30'}
                          `}
                        >
                          <input
                            type="radio"
                            name="subscription_plan"
                            value={plan.plan_id.replace('plan_', '')}
                            checked={formData.subscription_plan === plan.plan_id.replace('plan_', '')}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <p className="font-bold text-white">{plan.name}</p>
                            <p className="text-2xl font-black text-white mt-2">{getPlanPrice(plan)}</p>
                            <p className="text-xs text-zinc-400">pro Fahrzeug/Monat</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Jetzt kostenlos starten
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-zinc-500">
                    Mit der Registrierung akzeptieren Sie unsere AGB und Datenschutzerklärung
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Features Sidebar */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-700">
              <CardHeader>
                <CardTitle className="text-white">✨ Was Sie erhalten</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">14 Tage kostenlos</p>
                    <p className="text-sm text-zinc-400">Keine Kreditkarte erforderlich</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">GPS Live-Tracking</p>
                    <p className="text-sm text-zinc-400">Fahrzeuge in Echtzeit verfolgen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Digitales Fahrtenbuch</p>
                    <p className="text-sm text-zinc-400">Automatisch & rechtskonform</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Mobile App (PWA)</p>
                    <p className="text-sm text-zinc-400">Auf allen Geräten nutzbar</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Deutscher Support</p>
                    <p className="text-sm text-zinc-400">Schnelle Hilfe bei Fragen</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="pt-6 text-center">
                <p className="text-zinc-400 text-sm mb-4">Bereits registriert?</p>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Zum Login
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantRegister;
